// Open-Meteo: free, no API key, HTTPS, CORS-enabled.
// Docs: https://open-meteo.com/en/docs
// HTTPS is required (iOS ATS); never change this to http.
const OPEN_METEO_URL = 'https://api.open-meteo.com/v1/forecast';

// Simple in-memory cache so we don't refetch the same field's forecast on
// every map click. Keyed by rounded lat,lng + calendar day.
const cache = new Map();

function cacheKey(lat, lng) {
  const day = new Date().toISOString().slice(0, 10);
  // Round to ~110m so adjacent distinct fields don't collide while still
  // letting tiny centroid jitter hit the cache.
  return `${lat.toFixed(3)},${lng.toFixed(3)},${day}`;
}

/**
 * Fetch a 7-day daily forecast for a coordinate.
 * Returns a normalized array of day objects, or throws a normalized Error.
 *
 * Shape of each day:
 *   { date, tempMax, tempMin, precipSum, precipProbability, windMax, weatherCode }
 */
export async function getDailyForecast(lat, lng) {
  if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
    throw new Error('weather_invalid_coordinates');
  }

  const key = cacheKey(lat, lng);
  if (cache.has(key)) {
    return cache.get(key);
  }

  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lng),
    daily: [
      'temperature_2m_max',
      'temperature_2m_min',
      'precipitation_sum',
      'precipitation_probability_max',
      'wind_speed_10m_max',
      'weather_code'
    ].join(','),
    timezone: 'auto',
    forecast_days: '7'
  });

  // Use fetch + AbortController for the timeout, matching the app's existing
  // fetch-based service style (UserService.js) and the timeout pattern in
  // Dashboard.js.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);

  let response;
  try {
    response = await fetch(`${OPEN_METEO_URL}?${params.toString()}`, {
      signal: controller.signal
    });
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error('weather_timeout');
    }
    throw new Error('weather_unavailable');
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    throw new Error('weather_unavailable');
  }

  let data;
  try {
    data = await response.json();
  } catch (err) {
    throw new Error('weather_unavailable');
  }

  const daily = data && data.daily;
  if (!daily || !Array.isArray(daily.time) || daily.time.length === 0) {
    throw new Error('weather_no_data');
  }

  const days = daily.time.map((date, i) => ({
    date,
    tempMax: pick(daily.temperature_2m_max, i),
    tempMin: pick(daily.temperature_2m_min, i),
    precipSum: pick(daily.precipitation_sum, i),
    precipProbability: pick(daily.precipitation_probability_max, i),
    windMax: pick(daily.wind_speed_10m_max, i),
    weatherCode: pick(daily.weather_code, i)
  }));

  cache.set(key, days);
  return days;
}

function pick(arr, i) {
  if (!Array.isArray(arr)) return null;
  const v = arr[i];
  return v === undefined ? null : v;
}

/**
 * Map a WMO weather code to a stable i18n key suffix + emoji icon.
 * Codes: https://open-meteo.com/en/docs (WMO Weather interpretation codes)
 */
export function describeWeatherCode(code) {
  if (code === null || code === undefined) {
    return { key: 'unknown', icon: '❓' };
  }
  if (code === 0) return { key: 'clear', icon: '☀️' };
  if (code === 1 || code === 2) return { key: 'partly_cloudy', icon: '⛅' };
  if (code === 3) return { key: 'overcast', icon: '☁️' };
  if (code === 45 || code === 48) return { key: 'fog', icon: '🌫️' };
  if (code >= 51 && code <= 57) return { key: 'drizzle', icon: '🌦️' };
  if (code >= 61 && code <= 67) return { key: 'rain', icon: '🌧️' };
  if (code >= 71 && code <= 77) return { key: 'snow', icon: '🌨️' };
  if (code >= 80 && code <= 82) return { key: 'rain_showers', icon: '🌧️' };
  if (code >= 85 && code <= 86) return { key: 'snow_showers', icon: '🌨️' };
  if (code >= 95) return { key: 'thunderstorm', icon: '⛈️' };
  return { key: 'unknown', icon: '❓' };
}

// Exposed for tests / cache busting if ever needed.
export function _clearWeatherCache() {
  cache.clear();
}
